// @flow
import React, { Component } from "react";
import { bindActionCreators, type Dispatch } from "redux";
import { connect } from "react-redux";
import { get, noop, attempt, isEqual } from "lodash";
import axios from "axios";
import tvgConf from "@tvg/conf";
import mediator from "@tvg/mediator";
import type { UnaryFn, QuinFn } from "@tvg/types/Functional";
import type { RaceProgram, Video, RaceVideoFeedback } from "@tvg/types/Race";
import VideoTpl from "@tvg/atomic-ui/_templates/RaceVideo";
import performanceUtils from "@tvg/utils/performanceUtils";
import initVideoStream from "@tvg/utils/videoStreamUtils";
import {
  setRaceSelectorModalStateAction,
  fixWagerSelector,
  fixHandicapSection,
  showRaceNavigationHeader,
  showVideoButtonHeader,
  type Actions as ProgramActions
} from "@tvg/shared-actions/ProgramActions";

import {
  togglePoolsNInfoModal,
  type Actions as PoolsNInfoAction
} from "../actions/poolsNInfo";
import {
  showVideo,
  closeVideo,
  getStreamSrc,
  type Actions as VideoActions
} from "../actions/video";

const prepareVideoGTMPayload = (
  video,
  channel,
  currentRace,
  isReplay: boolean
) => {
  let videoType = "Live Video";

  if (isReplay) {
    videoType = "Race Replay";
  } else if (channel) {
    videoType = "Live Stream";
  }

  return {
    trackName: get(currentRace, "track.trackName", ""),
    raceNumber: get(currentRace, "raceNumber", ""),
    runnerType: get(currentRace, "isGreyhound", false) ? "Greyhound" : "Horse",
    videoType,
    videoProvider: "RCN"
  };
};

export type Props = {
  dispatch: Dispatch<ProgramActions | VideoActions | PoolsNInfoAction>,
  getStreamSrc: QuinFn<
    string,
    string,
    boolean,
    boolean,
    ?UnaryFn<?{ message: string, code: Number }, void>,
    Promise<mixed>
  >,
  video: Video,
  isUserLogged: boolean,
  userBalance: number,
  isBalanceShown: boolean,
  currentRace: RaceProgram,
  feedback: RaceVideoFeedback,
  stream: string,
  channel: string,
  isVideoInline: boolean,
  isSticky: boolean,
  raceReplaysToggle: boolean,
  videoFullscreenTilt: boolean,
  hasNewStreamVideo: boolean,
  enableHDVideo: boolean,
  openVideoFromMyBets: boolean,
  enablePawsQuickDeposits: boolean
};

type State = {
  isPlaying: boolean,
  isStreamsFetched: boolean,
  shouldStreamReplay: boolean
};

export class RaceVideoComponent extends Component<Props, State> {
  static defaultProps = {
    getStreamSrc: noop,
    isUserLogged: false,
    userBalance: 0,
    isBalanceShown: false,
    channel: "",
    stream: "",
    isVideoInline: false,
    isSticky: false,
    raceReplaysToggle: false,
    videoFullscreenTilt: false,
    hasNewStreamVideo: false,
    enableHDVideo: false,
    openVideoFromMyBets: false,
    enablePawsQuickDeposits: false
  };

  replayFetch: IntervalID;

  constructor(props: Props) {
    super(props);

    this.state = {
      isPlaying: false,
      isStreamsFetched: false,
      shouldStreamReplay: false,
      replayFetch: null
    };
  }

  componentDidMount() {
    const { isVideoInline } = this.props;

    if (!isVideoInline) {
      this.onWatchVideo();
    }
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    const mtpChanged = !isEqual(
      this.props.currentRace.mtp,
      nextProps.currentRace.mtp
    );
    const raceStatusChanged = !isEqual(
      get(this.props, "currentRace.status.code"),
      get(nextProps, "currentRace.status.code")
    );

    return (
      this.props.stream !== nextProps.stream ||
      !isEqual(this.props.video, nextProps.video) ||
      !isEqual(
        this.props.currentRace.raceNumber,
        nextProps.currentRace.raceNumber
      ) ||
      this.state.shouldStreamReplay !== nextState.shouldStreamReplay ||
      !isEqual(this.props.currentRace.video, nextProps.currentRace.video) ||
      !isEqual(this.props.currentRace.track, nextProps.currentRace.track) ||
      mtpChanged ||
      raceStatusChanged ||
      this.props.channel !== nextProps.channel ||
      this.props.feedback !== nextProps.feedback ||
      this.props.userBalance !== nextProps.userBalance ||
      this.props.isBalanceShown !== nextProps.isBalanceShown ||
      this.props.isUserLogged !== nextProps.isUserLogged
    );
  }

  static getDerivedStateFromProps(props: Props, prevState: State) {
    const replayFile =
      get(props.currentRace, "video.mobileAvailable") &&
      get(props.currentRace, "video.replayFileName");

    const shouldStreamReplay = !!replayFile;

    if (
      props.raceReplaysToggle &&
      prevState.shouldStreamReplay !== shouldStreamReplay
    ) {
      return {
        ...prevState,
        shouldStreamReplay
      };
    }

    return null;
  }

  componentDidUpdate(prevProps: Props) {
    const {
      isVideoInline,
      video,
      raceReplaysToggle,
      dispatch,
      currentRace,
      isUserLogged,
      feedback,
      channel
    } = this.props;

    // clear fetch replay poller
    if (video.src.replay || prevProps.currentRace.id !== currentRace.id) {
      this.clearFetchReplay();
    }

    if (!isUserLogged) {
      this.onCloseVideo(false);
    }

    // state for tablet and not inline for mobile
    const isPlaying = this.state.isPlaying || (!isVideoInline && video.show);

    if (isVideoInline && video.show && !this.state.isStreamsFetched) {
      this.onWatchVideo();
    }

    if (
      isPlaying &&
      get(this.props, "currentRace.track.trackCode", "") !==
        get(prevProps, "currentRace.track.trackCode", "")
    ) {
      if (!channel) {
        this.onCloseVideo(false);
        if (feedback.indexOf("AVAILABLE") === 0) this.onWatchVideo();
      }
    } else if (
      raceReplaysToggle &&
      isPlaying &&
      this.shouldFetchReplay(prevProps)
    ) {
      this.fetchReplay();
      this.clearFetchReplay();
      this.replayFetch = setInterval(this.fetchReplay, 30000);
    } else if (
      video.src.replay &&
      prevProps.currentRace.id !== currentRace.id
    ) {
      dispatch({
        type: "VIDEO_REQUEST_SUCCESS",
        payload: {
          src: {
            sd: video.src.sd,
            hd: video.src.hd,
            replay: null
          },
          show: video.show
        }
      });
    }
  }

  componentWillUnmount() {
    if (this.props.videoFullscreenTilt) {
      mediator.ios.dispatch({
        type: "IPHONE_LOCK_SCREEN_ROTATION",
        payload: { lockScreenRotation: 0 }
      });
    }
    this.onCloseVideo(false);
    this.clearFetchReplay();
  }

  onWatchVideo = () => {
    const { dispatch, currentRace, channel, raceReplaysToggle } = this.props;
    const isMobileVideoAvailable = !!get(currentRace, "video.mobileAvailable");
    const isStreamHighDefinition =
      this.props.enableHDVideo &&
      !!get(currentRace, "video.isStreamHighDefinition");

    const stream = !channel
      ? get(currentRace, "video.streams[0]", "")
      : channel;

    const replayFile =
      raceReplaysToggle && isMobileVideoAvailable
        ? get(currentRace, "video.replayFileName", null) || ""
        : "";

    this.setState({
      isStreamsFetched: true
    });

    initVideoStream(
      stream,
      replayFile,
      !!channel,
      true,
      isStreamHighDefinition,
      this.props.getStreamSrc,
      this.onVideoError
    );

    this.openVideoOffset();
    dispatch(showRaceNavigationHeader(false));
    performanceUtils.register("video_start");

    mediator.base.dispatch({
      type: "VIDEO:OPEN",
      payload: {
        module: "PP Header",
        videoType: this.state.shouldStreamReplay ? "Replay" : "Live",
        trackName: get(this.props, "currentRace.track.trackName"),
        raceNumber: get(this.props, "currentRace.raceNumber"),
        videoProvider: "RCN"
      }
    });

    this.setState({
      isPlaying: true
    });
  };

  onVideoStart = () => {
    const { channel, currentRace } = this.props;
    const track = channel || get(currentRace, "track.trackCode");

    performanceUtils.measure("video_start", { track, error: "false" });
  };

  onCloseVideo = (closedManually: boolean = true) => {
    const { dispatch } = this.props;

    if (closedManually) {
      // gtm event - open race selector
      mediator.base.dispatch({
        type: "VIDEO:CLOSE",
        payload: {
          module: "PP Header",
          videoType: this.state.shouldStreamReplay ? "Replay" : "Live",
          trackName: get(this.props, "currentRace.track.trackName"),
          raceNumber: get(this.props, "currentRace.raceNumber"),
          videoProvider: "RCN"
        }
      });
    }

    dispatch(closeVideo());
    dispatch(showRaceNavigationHeader(false));
    dispatch(showVideoButtonHeader(false));
    dispatch(fixWagerSelector({ isWagerSelectorFixed: false, fixedOffset: 0 }));

    this.setState({
      isPlaying: false,
      isStreamsFetched: false,
      shouldStreamReplay: false
    });

    if (typeof window !== "undefined") {
      window.scroll({
        top: 0,
        left: 0,
        behavior: "smooth"
      });
    }
    if (this.props.videoFullscreenTilt) {
      mediator.ios.dispatch({
        type: "IPHONE_LOCK_SCREEN_ROTATION",
        payload: { lockScreenRotation: 0 }
      });
    }
  };

  onRaceNavigation = () => {
    const { dispatch, currentRace } = this.props;

    dispatch(setRaceSelectorModalStateAction(true));
    // gtm event - open race selector
    mediator.base.dispatch({
      type: "PPM_OPEN_RACE_NAVIGATION",
      payload: {
        trackName: get(currentRace, "track.trackName"),
        raceNumber: get(currentRace, "raceNumber"),
        module: "Video"
      }
    });
  };

  onPoolsNInfo = () => {
    const { dispatch } = this.props;

    dispatch(togglePoolsNInfoModal(true));
    mediator.base.dispatch({
      type: "POOLS_INFO:OPEN_CLOSE",
      payload: {
        open: true,
        module: "Video"
      }
    });
    performanceUtils.register("page_load");
  };

  onQuickDeposit = () => {
    mediator.base.dispatch({
      type: "DEPOSITS_OPEN_QUICK",
      payload: { module: "Video" }
    });
  };

  onVideoMute = (muted: boolean = true) => {
    const { video, channel, currentRace } = this.props;
    const { shouldStreamReplay } = this.state;
    const payload = {
      ...prepareVideoGTMPayload(
        video,
        channel,
        currentRace,
        shouldStreamReplay
      ),
      muted
    };

    mediator.base.dispatch({
      type: "VIDEO:MUTE",
      payload
    });
  };

  onVideoFullScreen = (fullScreen: boolean = false, tilt: boolean = false) => {
    const { video, channel, currentRace } = this.props;
    const { shouldStreamReplay } = this.state;
    const payload = {
      ...prepareVideoGTMPayload(
        video,
        channel,
        currentRace,
        shouldStreamReplay
      ),
      fullScreen,
      tilt
    };

    mediator.base.dispatch({
      type: "VIDEO:FULL_SCREEN",
      payload
    });
  };

  onVideoPlay = () => {
    const { video, channel, currentRace } = this.props;
    const { shouldStreamReplay } = this.state;
    const payload = prepareVideoGTMPayload(
      video,
      channel,
      currentRace,
      shouldStreamReplay
    );

    mediator.base.dispatch({
      type: "VIDEO:PLAY_VIDEO",
      payload
    });
    if (this.props.videoFullscreenTilt) {
      mediator.ios.dispatch({
        type: "IPHONE_LOCK_SCREEN_ROTATION",
        payload: { lockScreenRotation: 2 }
      });
    }
  };

  onVideoPause = () => {
    const { video, channel, currentRace } = this.props;
    const { shouldStreamReplay } = this.state;
    const payload = prepareVideoGTMPayload(
      video,
      channel,
      currentRace,
      shouldStreamReplay
    );

    mediator.base.dispatch({
      type: "VIDEO:STOP_VIDEO",
      payload
    });
  };

  onVideoSuccess = () => {
    const { video, channel, currentRace } = this.props;
    const { shouldStreamReplay } = this.state;
    const payload = prepareVideoGTMPayload(
      video,
      channel,
      currentRace,
      shouldStreamReplay
    );

    mediator.base.dispatch({
      type: "VIDEO:SUCCESS",
      payload
    });
  };

  onNavigateVideo = (forward: boolean = true) => {
    const { video, channel, currentRace } = this.props;
    const { shouldStreamReplay } = this.state;
    const payload = {
      ...prepareVideoGTMPayload(
        video,
        channel,
        currentRace,
        shouldStreamReplay
      ),
      forward
    };

    mediator.base.dispatch({
      type: "VIDEO:NAVIGATE",
      payload
    });
  };

  onSwitchVideoType = (switchToLive: boolean = true) => {
    const { video, channel, currentRace } = this.props;
    const { shouldStreamReplay } = this.state;
    const payload = {
      ...prepareVideoGTMPayload(
        video,
        channel,
        currentRace,
        shouldStreamReplay
      ),
      switchToLive
    };

    mediator.base.dispatch({
      type: "VIDEO:SWITCH_STREAMS",
      payload
    });
  };

  onVideoError = (error: ?{ message: string, code: Number }) => {
    const { channel, currentRace } = this.props;
    const track = channel || get(currentRace, "track.trackCode");
    performanceUtils.measure("video_start", { track, error: "true" });

    const accountId = attempt(() => sessionStorage.getItem("userId"));
    const errorCode = [
      "UNKNOWN_ERROR",
      "MEDIA_ERR_ABORTED",
      "MEDIA_ERR_NETWORK",
      "MEDIA_ERR_DECODE",
      "MEDIA_ERR_SRC_NOT_SUPPORTED",
      "MEDIA_ERR_ENCRYPTED"
    ];

    axios({
      url: "/mbl/logerror",
      method: "post",
      withCredentials: true,
      data: {
        msg: `Error streaming video for track: ${track} with source: ${get(
          this.props,
          "video.src.sd"
        )}`,
        error: {
          message: get(error, "message", error),
          code: errorCode[get(error, "code", 0)]
        },
        accountId,
        tvgContext: tvgConf().context()
      }
    });
    if (this.props.videoFullscreenTilt) {
      mediator.ios.dispatch({
        type: "IPHONE_LOCK_SCREEN_ROTATION",
        payload: { lockScreenRotation: 0 }
      });
    }
  };

  onVideoErrorGTM = (error: string) => {
    const { video, channel, currentRace } = this.props;
    const { shouldStreamReplay } = this.state;

    const payload = {
      ...prepareVideoGTMPayload(
        video,
        channel,
        currentRace,
        shouldStreamReplay
      ),
      error
    };

    mediator.base.dispatch({
      type: "VIDEO:ERROR",
      payload
    });
  };

  onVideoOpen = () => {
    const { dispatch, isUserLogged } = this.props;

    const setVideoOpen = () => {
      dispatch(showVideo());
      this.setState({
        isPlaying: true
      });
    };

    if (!isUserLogged) {
      mediator.base.dispatch({
        type: "OPEN_LOGIN",
        payload: {
          callback: (error, success) => {
            if (success) {
              setVideoOpen();
            }
          },
          triggerAction: "race_video_open"
        }
      });
    } else {
      setVideoOpen();
    }
  };

  clearFetchReplay = () => {
    const replayInterval = this.replayFetch;
    if (replayInterval) {
      clearInterval(replayInterval);
    }
  };

  reloadStreams = () => {
    const { currentRace, channel, raceReplaysToggle } = this.props;
    const isMobileVideoAvailable = !!get(currentRace, "video.mobileAvailable");
    const isStreamHighDefinition =
      this.props.enableHDVideo &&
      !!get(currentRace, "video.isStreamHighDefinition");

    const stream = !channel
      ? get(currentRace, "video.streams[0]", "")
      : channel;

    const replayFile =
      raceReplaysToggle && isMobileVideoAvailable
        ? get(currentRace, "video.replayFileName", null) || ""
        : "";

    return initVideoStream(
      stream,
      replayFile,
      !!channel,
      true,
      isStreamHighDefinition,
      this.props.getStreamSrc,
      this.onVideoError
    );
  };

  shouldFetchReplay = (prevProps: Props): boolean => {
    const currentRaceVideo = get(this.props.currentRace, "video");
    const previousRaceVideo = get(prevProps.currentRace, "video");
    const replayLoaded = get(this.props, "video.src.replay");
    const isMobileVideoAvailable = !!get(
      this.props.currentRace,
      "video.mobileAvailable"
    );

    return (
      this.props.raceReplaysToggle &&
      isMobileVideoAvailable &&
      currentRaceVideo.replayFileName !== null &&
      (currentRaceVideo.replayFileName !== previousRaceVideo.replayFileName ||
        !replayLoaded)
    );
  };

  fetchReplay = () => {
    const { currentRace } = this.props;
    const isMobileVideoAvailable = !!get(currentRace, "video.mobileAvailable");
    const isStreamHighDefinition =
      this.props.enableHDVideo &&
      !!get(currentRace, "video.isStreamHighDefinition");

    initVideoStream(
      "",
      isMobileVideoAvailable
        ? get(currentRace, "video.replayFileName", "")
        : "",
      false,
      true,
      isStreamHighDefinition,
      this.props.getStreamSrc,
      this.onVideoError
    );
  };

  deviceLockRotationToggle = (lock: boolean = true) => {
    if (this.props.videoFullscreenTilt) {
      mediator.ios.dispatch({
        type: "IPHONE_LOCK_SCREEN_ROTATION",
        payload: { lockScreenRotation: lock ? 0 : 2 }
      });
    }
  };

  currentRaceBuilder = () => {
    const { currentRace } = this.props;

    return currentRace
      ? {
          mtp: get(currentRace, "mtp"),
          raceNumber: get(currentRace, "raceNumber"),
          trackAbbr: get(currentRace, "track.trackCode"),
          trackName: get(currentRace, "track.shortName"),
          status: get(currentRace, "status.code"),
          postTime: get(currentRace, "postTime"),
          isGreyhound: get(currentRace, "type.code") === "G"
        }
      : null;
  };

  quickDepositBuilder = () => {
    const { userBalance, isBalanceShown, enablePawsQuickDeposits } = this.props;

    return {
      balance: userBalance,
      isBalanceShown,
      url: `?race=${get(this.props, "currentRace.raceNumber", "1")}#${
        enablePawsQuickDeposits ? `quick-deposit` : `deposit`
      }`,
      ...(!enablePawsQuickDeposits ? { onClick: this.onQuickDeposit } : {})
    };
  };

  openVideoOffset() {
    if (typeof window !== "undefined") {
      const fixedOffset = (window.innerWidth * 9) / 16;
      this.props.dispatch(
        fixWagerSelector({ isWagerSelectorFixed: true, fixedOffset })
      );
      this.props.dispatch(
        fixHandicapSection({
          isHandicapFixed: false,
          fixedOffset: 0,
          handicapFormatHeight: 24
        })
      );
    }
  }

  render() {
    const {
      video,
      feedback,
      isVideoInline,
      isSticky,
      channel,
      currentRace,
      videoFullscreenTilt
    } = this.props;
    const { shouldStreamReplay } = this.state;
    const showLiveVideo = feedback && feedback.indexOf("AVAILABLE") === 0;
    const isMobileVideoAvailable = !!get(currentRace, "video.mobileAvailable");

    const videoSRC = {
      ...video.src,
      sd: showLiveVideo ? video.src.sd : null,
      hd: showLiveVideo ? video.src.hd : null,
      replay: !channel ? video.src.replay : null
    };

    const showVideoReplayMode = shouldStreamReplay && isMobileVideoAvailable;

    return (
      <VideoTpl
        videoSRC={videoSRC}
        isReplay={showVideoReplayMode}
        feedback={feedback}
        isVideoInline={isVideoInline}
        isVideoInlinePlaying={video.show}
        isSticky={isSticky}
        raceInfo={this.currentRaceBuilder()}
        onOpen={this.onVideoOpen}
        onClose={this.onCloseVideo}
        quickDeposit={this.quickDepositBuilder()}
        videoStartCallback={this.onVideoStart}
        onVideoSuccess={this.onVideoSuccess}
        videoErrorCallback={this.onVideoError}
        onVideoError={this.onVideoErrorGTM}
        onRaceNavigation={this.onRaceNavigation}
        onPoolsNInfo={this.onPoolsNInfo}
        onVideoMute={this.onVideoMute}
        onVideoFullScreen={this.onVideoFullScreen}
        onVideoPlay={this.onVideoPlay}
        onVideoPause={this.onVideoPause}
        videoFullscreenTilt={videoFullscreenTilt}
        deviceLockRotationToggle={this.deviceLockRotationToggle}
        reloadStreams={this.reloadStreams}
        onSwitchVideoType={this.onSwitchVideoType}
        onNavigateVideo={this.onNavigateVideo}
        hasNewStreamVideo={this.props.hasNewStreamVideo}
      />
    );
  }
}

export default connect(
  (store) => ({
    video: store.RaceProgramVideo,
    isUserLogged: store.userData.logged,
    userBalance: store.userData.balance,
    isBalanceShown:
      get(store, "userData.preferences.balance_is_shown", "0") === "1",
    raceReplaysToggle: get(store, "capi.featureToggles.raceReplay", false),
    hasNewStreamVideo: get(
      store,
      "capi.featureToggles.hasNewStreamVideo",
      false
    ),
    enableHDVideo: get(store, "capi.featureToggles.hasHdVideo", false),
    videoFullscreenTilt:
      get(store, "capi.featureToggles.videoFullscreenTilt", false) &&
      !(tvgConf().device === "tablet" && tvgConf().product === "ios2"),
    enablePawsQuickDeposits: get(
      store,
      "capi.featureToggles.pawsQuickDeposit",
      false
    )
  }),
  (dispatch: Dispatch<*>) => ({
    dispatch,
    // $FlowFixMe
    ...bindActionCreators({ getStreamSrc }, dispatch)
  })
)(RaceVideoComponent);
